///
/// EarthBrowser
///
/// Created by Matt Giger on 4/6/2024
/// Copyright (c) 2024 Geodata Labs
///

import { type TileInterface, type EarthTile } from "@/earthbrowser/activities/EarthActivity/tilecache/EarthTile"
import { EarthTileSet } from "@/earthbrowser/activities/EarthActivity/tilecache/EarthTileSet"
import { TileId } from "@/earthbrowser/activities/EarthActivity/tilecache/TileId"
import { type EarthBrowserContext } from "@/earthbrowser/EarthBrowserContext"
import { engineBridge } from "@/earthbrowser/engineBridge"
import { geographic, wgs } from "@/earthbrowser/utils/geographic"
import { type Camera } from "@stovokor/camera/Camera"
import { type Perspective } from "@stovokor/camera/Perspective"
import { trig } from "@stovokor/math/math"
import { sphere } from "@stovokor/math/sphere"
import { dot, normal, vec3 } from "@stovokor/math/vector"
import { type Stovokor } from "@stovokor/Stovokor"
import { type TimerTask } from "@stovokor/tasks/TimerTask"
import { type WebMercatorOverlayTile } from "./WebMercatorOverlayTile"
import { WebMercatorOverlayTileSet } from "./WebMercatorOverlayTileSet"
import { WebMercatorTileLevel } from "./WebMercatorTileLevel"

const cEndingLevel				= 12

const cUpdateFrequency			= .05
const cTileScreenSize			= 220		// Split a tile when it projects larger than this (px), ~one texel per pixel
const cForeshortenFloor			= 0.2		// Minimum facing factor, limits how coarse grazing-angle tiles may stay
const cHorizonMargin			= 0.01		// Keep tiles up to this far (radians) past the geometric horizon
const cMaxTilesLoading			= 100		// Allow more tiles to load in parallel
const cLeafBudget				= 140		// Max traversal leaves (tiles actually showing pixels)
const cMaxTilesAllowed			= 250		// Render cap: leaves plus their ancestor pyramid and loading transients
const cPurgableTimeSecs			= 3			// Keep tiles around a bit longer
const cRelevantDistance			= wgs.a * 0.0004
const cMaxMovingUpdateTime		= 2
const cMaxMovingUpdateDistance	= wgs.a * 0.03

// Web Mercator overlay tile selection (radar refinement tiles). The viewport
// is sampled on a ray grid; each globe hit increments the containing tile's
// bin, and bins over the threshold become wanted tiles.
const cWmSampleGrid				= 16		// Viewport sample grid (16x16 rays)
const cWmHitThreshold			= 2			// Bins with fewer viewport sample hits are ignored
const cWmZoomFactor				= 40000		// level = log2(factor / altitudeKm), same scale as vector tiles
const cWmMinLevel				= 5			// Below z5 the 4096x2048 equirect mosaic out-resolves the tiles
const cWmMaxLevel				= 7			// Server archives are cut to z7 (~source native resolution)

/**
 * A tile the camera currently wants, produced by the quadtree traversal.
 * Includes both render leaves and their ancestors (fallback imagery and
 * fade/elevation parents for their children).
 */
export class TileCandidate {
	constructor(
		public readonly id: TileId,
		public readonly centerDistance: number		// 1 - cos(angle to the screen center point), smaller is closer
	) {}
}

export class EarthTileUpdateTask implements TimerTask {
	public id: number = 0
	public delay: number = 0.4
	public nextActivation: number = 0
	public dead: boolean = false
	private globe = new sphere(wgs.a)
	private engine!: Stovokor
	private renderingTileSet: EarthTileSet
	private wmTileSet: WebMercatorOverlayTileSet
	private lastUpdateTime: number = 0
	private lastPosition = vec3.zero

	constructor(context: EarthBrowserContext, baseLevel: number) {
		this.renderingTileSet = new EarthTileSet(context, baseLevel, cMaxTilesLoading, cPurgableTimeSecs)
		this.wmTileSet = new WebMercatorOverlayTileSet(context, cWmHitThreshold, cMaxTilesLoading, cPurgableTimeSecs)
	}

	public addBaseTile(tile: EarthTile): void {
		this.renderingTileSet.addBaseTile(tile)
	}

	public get renderingTiles(): EarthTile[] {
		return this.renderingTileSet.renderList(cMaxTilesAllowed)
	}

	public get wmRenderingTiles(): WebMercatorOverlayTile[] {
		return this.wmTileSet.renderable()
	}

	public get wmTileCount(): number {
		return this.wmTileSet.allTiles().length
	}

	public get wmTileSeries() {
		return this.wmTileSet.tileSeries
	}

	public get allTiles(): TileInterface[] {
		return [...this.renderingTileSet.allTiles(), ...this.wmTileSet.allTiles()]
	}

	/** Every cached earth tile, including ones not in the current render list. */
	public get allEarthTiles(): EarthTile[] {
		return this.renderingTileSet.allTiles()
	}

	/**
	 * Update WebMercator tile materials from the current TileSeries state.
	 * Call this each frame to update time-based animation.
	 */
	public updateWmMaterials(): void {
		this.wmTileSet.updateMaterialsFromSeries()
	}

	/** Drop all overlay tiles and clear the time series (layer hidden). */
	public clearWmTiles(): void {
		this.wmTileSet.destroyAll()
		this.wmTileSet.tileSeries.clear()
	}

	public start(engine: Stovokor): void {
		this.engine = engine
	}

	public action(_time: number): void {
		const now = performance.now() * 0.001

		// Skip expensive tile updates during rapid camera movement (e.g., slider dragging)
		// Section visibility is recalculated every frame in renderList, so no gaps appear
		const rapidCameraMovement = engineBridge.getState()?.dialog.rapidCameraMovement ?? false
		if (rapidCameraMovement) {
			return
		}

		// Radar refinement tiles update on every activation (cheap ray-grid
		// sampling): they must react to newly published frames and to time
		// scrubbing even when the camera is stationary
		this.updateWebMercatorTiles(this.engine.world.camera)

		// control the update frequency by the distance moved
		// if the camera is moving rapidly, update less frequently
		const rts = this.renderingTileSet
		const camera = this.engine.world.camera
		const position = camera.position
		const distance = position.subtract(this.lastPosition).length
		if(now < this.nextActivation && (distance < cRelevantDistance || (now - this.lastUpdateTime < cMaxMovingUpdateTime && distance < cMaxMovingUpdateDistance))) {
			rts.queueLoading()
			return
		}
		this.lastUpdateTime = now
		this.lastPosition = position
		this.nextActivation = now + cUpdateFrequency

		rts.updateWanted(this.selectTiles(camera))
		rts.pruneDormantTiles()
		rts.queueLoading()
	}

	/**
	 * Select and load Web Mercator overlay tiles (radar refinement) for the
	 * current view. Active only while the overlay tile series has frames and
	 * the camera is close enough that tiles out-resolve the global equirect
	 * mosaic. Samples the viewport on a ray grid, bins globe hits into
	 * mercator tiles at the altitude-derived zoom level, and feeds the bins
	 * to the overlay tile set for loading and pruning.
	 */
	private updateWebMercatorTiles(camera: Camera): void {
		if (!this.wmTileSet.tileSeries.hasFrames) {
			if (this.wmTileSet.allTiles().length > 0) {
				this.wmTileSet.destroyAll()
			}
			return
		}

		const altitudeKm = Math.max(camera.position.length - wgs.a, 1)
		const rawLevel = Math.round(Math.log2(cWmZoomFactor / altitudeKm))
		if (rawLevel < cWmMinLevel) {
			// Zoomed out: the mosaic overlay is sharper than the tiles.
			// Stop feeding bins so existing tiles age out via pruning
			this.wmTileSet.updatePriorities([])
			this.wmTileSet.pruneDormantTiles()
			return
		}
		const level = Math.min(rawLevel, cWmMaxLevel)

		// Surface point at the screen center, for load prioritization
		const width = camera.projection.width
		const height = camera.projection.height
		const centerRay = camera.rayFrom(width / 2, height / 2)
		const centerDist = this.globe.intersect(centerRay)
		const centerGeo = centerDist > 0 ? new geographic(centerRay.pointAt(centerDist)) : null

		// Bin viewport ray-grid samples into mercator tiles (slight margin
		// past the viewport edges so tiles are ready as the camera pans)
		const tileLevel = new WebMercatorTileLevel(level)
		for (let i = 0; i < cWmSampleGrid; i++) {
			const sy = (-0.05 + 1.1 * (i + 0.5) / cWmSampleGrid) * height
			for (let j = 0; j < cWmSampleGrid; j++) {
				const sx = (-0.05 + 1.1 * (j + 0.5) / cWmSampleGrid) * width
				const ray = camera.rayFrom(sx, sy)
				const dist = this.globe.intersect(ray)
				if (dist > 0) {
					tileLevel.addHit(new geographic(ray.pointAt(dist)), centerGeo)
				}
			}
		}

		this.wmTileSet.updatePriorities(Array.from(tileLevel.bins.values()))
		this.wmTileSet.pruneDormantTiles()
		this.wmTileSet.queueLoading()
	}

	/**
	 * Determine the set of tiles the camera wants by walking the quadtree from the
	 * base level, culling against the horizon and the view frustum, and refining
	 * while a tile's projected screen size exceeds the split threshold (screen-space
	 * error refinement). Every visited visible tile is a candidate, so a leaf's
	 * ancestors are always present to serve as loading parents and fallbacks.
	 *
	 * The budget is counted in traversal leaves - the tiles that actually show
	 * pixels. Interior (ancestor) tiles are fully covered by their children once
	 * those load, so they cost render-list slots but no screen quality. If the
	 * leaf count exceeds the budget (possible at tilted views stretching toward
	 * the horizon), the traversal is re-run with a proportionally larger
	 * threshold: degrading quality uniformly trims the marginal splits far from
	 * the camera first while keeping full foreground detail.
	 */
	private selectTiles(camera: Camera): TileCandidate[] {
		let threshold = cTileScreenSize
		let result = this.traverse(camera, threshold)
		for(let attempt = 0; attempt < 3 && result.leaves > cLeafBudget; attempt++) {
			threshold *= Math.sqrt(result.leaves / cLeafBudget) * 1.1
			result = this.traverse(camera, threshold)
		}
		return result.wanted
	}

	private traverse(camera: Camera, splitThreshold: number): { wanted: TileCandidate[], leaves: number } {
		const camPos = camera.position
		const camMag = camPos.length
		const sinCamLat = camPos.y / camMag
		const cosCamLat = Math.sqrt(Math.max(0, 1 - sinCamLat * sinCamLat))
		const camLon = Math.atan2(camPos.x, camPos.z)

		// A surface point at angular distance g from the sub-camera point is beyond
		// the horizon when cos(g) < a/|camera|. Widen the cap slightly so tiles
		// straddling the horizon are kept.
		const capAngle = Math.acos(Math.min(wgs.a / camMag, 1)) + cHorizonMargin
		const horizonCos = Math.cos(Math.min(capAngle, trig.pi))

		// View-space frustum, culled the same way NodeBase culls geometry
		const viewFrustum = camera.projectionMatrix.frustum()
		const viewMatrix = camera.viewMatrix

		// Projected pixels per km of extent at 1km distance
		const perspective = camera.projection as Perspective
		const pixelFactor = camera.projection.height / (2 * Math.tan(perspective.fov * 0.5 * trig.rad))

		// Surface direction at the screen center, for load prioritization
		const centerRay = camera.rayFrom(camera.projection.width / 2, camera.projection.height / 2)
		const centerDist = this.globe.intersect(centerRay)
		const centerNormal = centerDist > 0 ? normal(centerRay.pointAt(centerDist)) : normal(camPos)

		const wanted: TileCandidate[] = []
		let leaves = 0

		const visit = (id: TileId): void => {
			const delta = trig.pi / (1 << id.z)
			const latMax = trig.half_pi - id.y * delta
			const latMin = latMax - delta
			const lonMin = id.x * delta - trig.pi

			// Longitude of the tile rect nearest the camera (wrapped) and its offset L
			let lonNear = camLon
			let cosL = 1
			if(camLon < lonMin || camLon > lonMin + delta) {
				const dMin = Math.abs(wrapAngle(camLon - lonMin))
				const dMax = Math.abs(wrapAngle(camLon - lonMin - delta))
				lonNear = dMin < dMax ? lonMin : lonMin + delta
				cosL = Math.cos(Math.min(dMin, dMax))
			}

			// cos(angular distance) from the sub-camera point to rect latitude p is
			// f(p) = sin(camLat)sin(p) + cos(camLat)cos(L)cos(p) = R*cos(p - p*),
			// maximized at p* = atan2(sin(camLat), cos(camLat)cos(L)). Evaluating the
			// clamped optimum and both edges yields the rect's angularly nearest point.
			const b = cosCamLat * cosL
			const latStar = Math.atan2(sinCamLat, b)
			let latNear = Math.min(Math.max(latStar, latMin), latMax)
			let cosGamma = sinCamLat * Math.sin(latNear) + b * Math.cos(latNear)
			const fMin = sinCamLat * Math.sin(latMin) + b * Math.cos(latMin)
			if(fMin > cosGamma) {
				cosGamma = fMin
				latNear = latMin
			}
			const fMax = sinCamLat * Math.sin(latMax) + b * Math.cos(latMax)
			if(fMax > cosGamma) {
				cosGamma = fMax
				latNear = latMax
			}

			// Horizon cull: the entire tile is beyond the visible cap
			if(cosGamma < horizonCos) {
				return
			}

			// Frustum cull with the tile's bounding sphere
			const bounds = tileBoundingSphere(latMin, latMax, lonMin, delta)
			if(viewFrustum.intersects(bounds.transform(viewMatrix)) === "outside") {
				return
			}

			const mid = new geographic(lonMin + delta / 2, (latMin + latMax) / 2).normal
			wanted.push(new TileCandidate(id, 1 - dot(mid, centerNormal)))

			// Refine while the tile projects larger on screen than its texture resolves.
			// Distance is measured to the nearest surface point of the tile, so nearby
			// tiles refine deeply and tiles toward the horizon stay coarse. Tiles seen
			// at a grazing angle are compressed on screen, so their effective size is
			// scaled down by a softened facing factor (1 face-on, floor at the horizon).
			let refined = false
			if(id.z < cEndingLevel) {
				const nearPoint = new geographic(lonNear, latNear).cartesian
				const toTile = nearPoint.subtract(camPos)
				const dist = Math.max(toTile.length, 1e-3)
				const facing = Math.max(-dot(toTile, nearPoint) / (dist * wgs.a), cForeshortenFloor)
				if(wgs.a * delta * pixelFactor * Math.sqrt(facing) / dist > splitThreshold) {
					refined = true
					id.children.forEach(visit)
				}
			}
			if(!refined) {
				leaves++
			}
		}

		const baseLevel = this.renderingTileSet.baseLevel
		const rows = 1 << baseLevel
		for(let y = 0; y < rows; y++) {
			for(let x = 0; x < 2 * rows; x++) {
				visit(new TileId(baseLevel, y, x))
			}
		}
		return { wanted, leaves }
	}
}

// Wrap an angle to (-pi, pi]
function wrapAngle(a: number): number {
	return a - trig.two_pi * Math.round(a / trig.two_pi)
}

/**
 * Bounding sphere of a lat/lon rect on the globe surface: centered on the rect's
 * surface midpoint, radius covering corner and edge-midpoint samples with a small
 * margin for the spherical bulge between samples.
 */
function tileBoundingSphere(latMin: number, latMax: number, lonMin: number, delta: number): sphere {
	const latMid = (latMin + latMax) / 2
	const lonMid = lonMin + delta / 2
	const center = new geographic(lonMid, latMid).cartesian
	let radius2 = 0
	for(const lat of [latMin, latMid, latMax]) {
		for(const lon of [lonMin, lonMid, lonMin + delta]) {
			if(lat === latMid && lon === lonMid) {
				continue
			}
			const d = new geographic(lon, lat).cartesian.subtract(center)
			radius2 = Math.max(radius2, dot(d, d))
		}
	}
	return new sphere(Math.sqrt(radius2) * 1.05, center)
}
